import { getContentAri } from './get-content-ari';

/**
 * Resolves a slug to a full URL using the current hostname
 *
 * @param slug - The slug path (e.g., "/bitbucket-cloud/docs/learn-how-to-use-commits/")
 * @returns The full URL or the slug if running on server-side
 */
export const resolveSlugUrl = (slug: string): string => {
    // Return slug as-is during server-side rendering
    if (typeof window === 'undefined') {
        return slug;
    }

    // Use window.SAC_BASE_URL if available, otherwise fall back to current origin
    const baseUrl = window.SAC_BASE_URL || window.location.origin;

    // Ensure there's a slash between baseUrl and slug
    const normalizedSlug = slug.startsWith('/') ? slug : `/${slug}`;

    return `${baseUrl}${normalizedSlug}`;
};

/**
 * Resolves URL for analytics
 *
 * @param url - The URL to resolve (can be a full URL or a slug)
 * @returns The resolved URL or null if no URL is provided
 */
export const resolveAnalyticsUrl = (url?: string): string | null => {
    if (!url) {
        return null;
    }
    return url.startsWith('http') ? url : resolveSlugUrl(url);
};

/**
 * Determines if a URL is from a support.atlassian.com or atlassianpartners domain.
 *
 * Uses URL hostname parsing for reliability instead of regex, which avoids
 * false positives from lookalike domains.
 *
 * Matches:
 * - support.atlassian.com
 * - support.stg.internal.atlassian.com (and other environment variations)
 * - atlassianpartners.atlassian.net (and subdomains)
 *
 * @param url - The URL to check
 * @returns true if the URL is from a SAC or atlassianpartners hostname
 */
const isSacContentUrl = (url?: string): boolean => {
    if (!url) {
        return false;
    }
    try {
        const { hostname } = new URL(url);
        return (
            (hostname.startsWith('support.') &&
                hostname.endsWith('.atlassian.com')) ||
            hostname.includes('atlassianpartners.')
        );
    } catch {
        return false;
    }
};

interface ConditionalContentAriOptions {
    contentId: string | undefined;
    resolvedUrl: string | undefined;
    contentAriProperty?: string;
}

/**
 * Resolves the content ARI using a priority-based approach:
 * 1. If contentAriProperty exists, use it
 * 2. If id starts with "ari:", use it as-is
 * 3. If the URL is from support.atlassian.com or atlassianpartners, format id as Contentful ARI
 * 4. Otherwise, return undefined (can't resolve)
 *
 * @param options - Options object containing contentId, resolvedUrl, and contentAriProperty
 * @returns The appropriately formatted ARI, or undefined if it cannot be resolved
 */
export const getConditionalContentAri = ({
    contentId,
    resolvedUrl,
    contentAriProperty
}: ConditionalContentAriOptions): string | undefined => {
    // Priority 1: Use content ARI property if available
    if (contentAriProperty) {
        return contentAriProperty;
    }

    if (!contentId) {
        return undefined;
    }

    // Priority 2: If ID already starts with "ari:", use it as-is
    if (contentId.startsWith('ari:')) {
        return contentId;
    }

    // Priority 3: If URL is from support.atlassian.com or atlassianpartners, format as Contentful ARI
    if (isSacContentUrl(resolvedUrl)) {
        return getContentAri(contentId);
    }

    // Priority 4: Otherwise, return undefined (can't resolve the ARI)
    return undefined;
};
